// Layer: Page (UI) – chỉ render JSX, gọi hook domain từ features.
import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Button, Table, Avatar, Row, Col, Typography, Spin, Alert, Image } from 'antd';
import { UserOutlined } from '@ant-design/icons';
import { Edit, Download, FileText, Image as ImageIcon } from 'lucide-react';
import type { ColumnsType } from 'antd/es/table';
import type { PersonnelDetail } from '../types/personnelDetail';
import { formatRole } from '../utils/formatters';
import { AdditionalPersonnelInfo } from '../components/personnel/AdditionalPersonnelInfo';
import { usePersonalProfile } from '@/features/personnel/usePersonalProfile';
import { buildFullUrl } from '@/contexts/AvatarContext';
import styles from './PersonalProfilePage.module.css';

const { Text, Title } = Typography;

export const PersonalProfilePage: React.FC = () => {
  const navigate = useNavigate();
  
  const {
    detailData,
    loading,
    error,
    avatarUri,
    handleExport,
    formatFieldValue,
  } = usePersonalProfile();

  // UI handler - navigate đến trang cập nhật
  const handleUpdate = () => {
    navigate('/personal/profile/update');
  };

  // Columns định nghĩa cấu trúc bảng công việc hiện nay
  const currentJobColumns: ColumnsType<NonNullable<PersonnelDetail['currentJob']>> = [
    {
      title: 'CTY',
      dataIndex: 'company',
      key: 'company',
    },
    {
      title: 'Phòng ban',
      dataIndex: 'department',
      key: 'department',
    },
    {
      title: 'Chức vụ',
      dataIndex: 'position',
      key: 'position',
      render: (text: string) => formatRole(text),
    },
    {
      title: 'Vị trí',
      dataIndex: 'role',
      key: 'role',
    },
    {
      title: 'Mức lương',
      dataIndex: 'salary',
      key: 'salary',
    },
    {
      title: 'Ngày vào làm',
      dataIndex: 'startDate',
      key: 'startDate',
    },
  ];

  type TrainingPlace = NonNullable<PersonnelDetail['trainingPlaces']>[0];
  type WorkExperience = NonNullable<PersonnelDetail['workExperience']>[0];
  type FamilyMember = NonNullable<PersonnelDetail['familyMembers']>[0];
  type Certificate = NonNullable<PersonnelDetail['certificates']>[0];
  type PartyHistory = NonNullable<PersonnelDetail['partyHistory']>[0];

  // Columns định nghĩa cấu trúc bảng nơi đào tạo
  const trainingColumns: ColumnsType<TrainingPlace> = [
    {
      title: 'Nơi đào tạo',
      dataIndex: 'place',
      key: 'place',
    },
    {
      title: 'Chuyên ngành',
      dataIndex: 'major',
      key: 'major',
    },
    {
      title: 'Hình thức đào tạo',
      dataIndex: 'type',
      key: 'type',
    },
    {
      title: 'Trình độ',
      dataIndex: 'degree',
      key: 'degree',
    },
    {
      title: 'Thời gian',
      dataIndex: 'time',
      key: 'time',
    },
  ];

  // Columns định nghĩa cấu trúc bảng kinh nghiệm làm việc
  const experienceColumns: ColumnsType<WorkExperience> = [
    {
      title: 'Tên CTY',
      dataIndex: 'company',
      key: 'company',
    },
    {
      title: 'Vị trí',
      dataIndex: 'role',
      key: 'role',
    },
    {
      title: 'Phòng ban',
      dataIndex: 'department',
      key: 'department',
    },
    {
      title: 'Mức lương',
      dataIndex: 'salary',
      key: 'salary',
    },
    {
      title: 'Thời gian',
      dataIndex: 'time',
      key: 'time',
    },
  ];

  // Columns định nghĩa cấu trúc bảng thành viên gia đình
  const familyColumns: ColumnsType<FamilyMember> = [
    {
      title: 'Phụ thuộc',
      dataIndex: 'relationship',
      key: 'relationship',
      render: (relationship: string) => relationship || '—',
    },
    {
      title: 'Họ và tên',
      dataIndex: 'name',
      key: 'name',
    },
    {
      title: 'Năm sinh',
      dataIndex: 'birthYear',
      key: 'birthYear',
      render: (birthYear: number | null) => birthYear ?? '—',
    },
    {
      title: 'Số điện thoại',
      dataIndex: 'phone',
      key: 'phone',
    },
    {
      title: 'Nghề nghiệp',
      dataIndex: 'occupation',
      key: 'occupation',
    },
    {
      title: 'Nơi làm việc',
      dataIndex: 'workplace',
      key: 'workplace',
    },
    {
      title: 'Địa chỉ',
      dataIndex: 'address',
      key: 'address',
    },
  ];

  // Columns định nghĩa cấu trúc bảng chứng chỉ
  const certificateColumns: ColumnsType<Certificate> = [
    {
      title: 'Số hiệu',
      dataIndex: 'id',
      key: 'id',
    },
    {
      title: 'Đơn vị ban hành',
      dataIndex: 'issuingBody',
      key: 'issuingBody',
    },
    {
      title: 'Loại chứng chỉ',
      dataIndex: 'type',
      key: 'type',
    },
    {
      title: 'Thời gian',
      dataIndex: 'time',
      key: 'time',
    },
    {
      title: 'Ghi chú',
      dataIndex: 'notes',
      key: 'notes',
    },
    {
      title: 'Ảnh chứng chỉ',
      dataIndex: 'attachments',
      key: 'attachments',
      render: (attachments: Certificate['attachments']) => attachments?.length
        ? <Image.PreviewGroup>{attachments.map((item) => <Image key={item.id} src={buildFullUrl(item.fileUrl) || undefined} alt={item.fileName} width={64} />)}</Image.PreviewGroup>
        : 'Chưa có ảnh',
    },
  ];

  // Columns định nghĩa cấu trúc bảng lịch sử đảng viên
  const partyColumns: ColumnsType<PartyHistory> = [
    {
      title: 'Số thẻ',
      dataIndex: 'cardNumber',
      key: 'cardNumber',
    },
    {
      title: 'Hình thức',
      dataIndex: 'form',
      key: 'form',
    },
    {
      title: 'Thời gian',
      dataIndex: 'time',
      key: 'time',
    },
    {
      title: 'Nơi kết nạp',
      dataIndex: 'admissionPlace',
      key: 'admissionPlace',
    },
    {
      title: 'Nơi điều chuyển',
      dataIndex: 'transferPlace',
      key: 'transferPlace',
    },
  ];

  if (loading) {
    return (
      <div className={styles.container}>
        <div className={styles.loadingContainer}>
          <Spin size="large" />
        </div>
      </div>
    );
  }

  if (!detailData) {
    return (
      <div className={styles.container}>
        <div className={styles.loadingContainer}>
          {error ? (
            <Alert
              message="Lỗi"
              description={error}
              type="error"
              showIcon
            />
          ) : (
            <Text>Không tìm thấy thông tin hồ sơ</Text>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <div className={styles.headerLeft}>
        </div>
        <div className={styles.headerActions}>
          <Button
            type="default"
            icon={<Edit size={16} />}
            onClick={handleUpdate}
            className={styles.actionButton}
          >
            Cập nhật
          </Button>
          <Button
            type="default"
            icon={<Download size={16} />}
            onClick={handleExport}
            className={styles.actionButton}
          >
            Xuất file
          </Button>
        </div>
      </div>

      {error && (
        <Alert
          message="Lỗi"
          description={error}
          type="error"
          showIcon
          closable
          className={styles.errorAlert}
        />
      )}

      <div className={styles.contentContainer}>
        <div className={styles.sidebar}>
          <div className={styles.profileCard}>
            <Avatar 
              size={120} 
              src={avatarUri && avatarUri.trim() !== '' ? avatarUri : undefined}
              icon={!avatarUri || avatarUri.trim() === '' ? <UserOutlined /> : undefined}
              onError={() => false}
              className={styles.avatar} 
            />
            <Title level={4} className={styles.profileName}>
              {detailData.fullName || 'Chưa cập nhật tên'}
            </Title>
            <div className={styles.roleTag}>
              {detailData.currentJob?.role || detailData.jobTitle || detailData.position || 'Chưa cập nhật vị trí'}
            </div>
          </div>

          <div className={styles.sidebarSection}>
            <div className={styles.basicInfoList}>
              <div className={styles.basicInfoItem}>
                <Text className={styles.basicInfoLabel}>Mã nhân sự:</Text>
                <Text className={styles.basicInfoValue}>{detailData.employeeCode || 'Chưa cập nhật'}</Text>
              </div>
              <div className={styles.basicInfoItem}>
                <Text className={styles.basicInfoLabel}>Email:</Text>
                <Text className={styles.basicInfoValue}>{detailData.email || 'Chưa cập nhật'}</Text>
              </div>
              <div className={styles.basicInfoItem}>
                <Text className={styles.basicInfoLabel}>Ngày sinh:</Text>
                <Text className={styles.basicInfoValue}>{detailData.dateOfBirth || 'Chưa cập nhật'}</Text>
              </div>
              <div className={styles.basicInfoItem}>
                <Text className={styles.basicInfoLabel}>Giới tính:</Text>
                <Text className={styles.basicInfoValue}>
                  {formatFieldValue('gender', detailData.gender) || 'Chưa cập nhật'}
                </Text>
              </div>
              <div className={styles.basicInfoItem}>
                <Text className={styles.basicInfoLabel}>Số điện thoại:</Text>
                <Text className={styles.basicInfoValue}>{detailData.phone || 'Chưa cập nhật'}</Text>
              </div>
              <div className={styles.basicInfoItem}>
                <Text className={styles.basicInfoLabel}>Ngày gia nhập:</Text>
                <Text className={styles.basicInfoValue}>{detailData.startDate || 'Chưa cập nhật'}</Text>
              </div>
              <div className={styles.basicInfoItem}>
                <Text className={styles.basicInfoLabel}>Phòng ban:</Text>
                <Text className={styles.basicInfoValue}>
                  {detailData.currentJob?.department || 'Chưa cập nhật'}
                </Text>
              </div>
            </div>
          </div>

          <div className={styles.sidebarSection}>
            <Title level={5} className={styles.sidebarSectionTitle}>
              Thẻ định danh
            </Title>
            <div className={styles.imageGrid}>
              <div className={styles.imageItem}>
                <Text className={styles.imageLabel}>Ảnh CC/CCCD/CMT Mặt trước</Text>
                {detailData.idCardFront ? (
                  <Image
                    src={detailData.idCardFront}
                    alt="ID Card Front"
                    className={styles.idCardImage}
                    preview
                  />
                ) : (
                  <div className={styles.imagePlaceholder}>
                    <ImageIcon size={24} />
                  </div>
                )}
              </div>
              <div className={styles.imageItem}>
                <Text className={styles.imageLabel}>Ảnh CC/CCCD/CMT Mặt sau</Text>
                {detailData.idCardBack ? (
                  <Image
                    src={detailData.idCardBack}
                    alt="ID Card Back"
                    className={styles.idCardImage}
                    preview
                  />
                ) : (
                  <div className={styles.imagePlaceholder}>
                    <ImageIcon size={24} />
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className={styles.sidebarSection}>
            <Title level={5} className={styles.sidebarSectionTitle}>
              Ảnh hộ chiếu
            </Title>
            <div className={styles.imageGrid}>
              <div className={styles.imageItem}>
                {detailData.passportCover ? (
                  <Image
                    src={detailData.passportCover}
                    alt="Passport Cover"
                    className={styles.idCardImage}
                    preview
                  />
                ) : (
                  <div className={styles.imagePlaceholder}>
                    <ImageIcon size={24} />
                  </div>
                )}
              </div>
              <div className={styles.imageItem}>
                {detailData.passportPhoto ? (
                  <Image
                    src={detailData.passportPhoto}
                    alt="Passport Photo"
                    className={styles.idCardImage}
                    preview
                  />
                ) : (
                  <div className={styles.imagePlaceholder}>
                    <ImageIcon size={24} />
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className={styles.sidebarSection}>
            <Title level={5} className={styles.sidebarSectionTitle}>
              Lá số tử vi
            </Title>
            <div className={styles.imageItem}>
              {detailData.horoscope ? (
                <Image
                  src={detailData.horoscope}
                  alt="Horoscope"
                  className={styles.horoscopeImage}
                  preview
                />
              ) : (
                <div className={styles.largeImagePlaceholder}>
                  <ImageIcon size={32} />
                </div>
              )}
            </div>
          </div>

          <div className={styles.sidebarSection}>
            <Title level={5} className={styles.sidebarSectionTitle}>
              File lưu trữ
            </Title>
            <div className={styles.fileList}>
              {detailData.storedFiles && detailData.storedFiles.length > 0 ? (
                detailData.storedFiles.map((file, index) => (
                  <div key={index} className={styles.fileItem}>
                    <FileText size={16} className={styles.fileIcon} />
                    <Text className={styles.fileName}>{file.name || 'Chưa cập nhật tên file'}</Text>
                    {file.url ? (
                      <a
                        href={file.url}
                        download={file.name || 'download'}
                        target="_blank"
                        rel="noopener noreferrer"
                        aria-label={`Tải xuống ${file.name || 'file'}`}
                      >
                        <Download size={16} className={styles.downloadIcon} />
                      </a>
                    ) : (
                      <Download size={16} className={styles.downloadIcon} aria-disabled="true" />
                    )}
                  </div>
                ))
              ) : (
                <Text className={styles.emptyFileText}>Chưa cập nhật file nào</Text>
              )}
            </div>
          </div>
        </div>

        <div className={styles.mainContent}>
          <div className={styles.section}>
            <Title level={4} className={styles.sectionTitle}>
              Giới thiệu bản thân
            </Title>
            <Text className={styles.description}>
              {detailData.description || 'Chưa cập nhật mô tả'}
            </Text>
          </div>

          <div className={styles.section}>
            <Title level={4} className={styles.sectionTitle}>
              Thông tin chi tiết
            </Title>
            <Row gutter={[24, 16]}>
              <Col span={12}>
                <div className={styles.infoRow}>
                  <Text className={styles.infoLabel}>Dân tộc:</Text>
                  <Text className={styles.infoValue}>
                    {formatFieldValue('ethnicity', detailData.ethnicity) || 'Chưa cập nhật'}
                  </Text>
                </div>
                <div className={styles.infoRow}>
                  <Text className={styles.infoLabel}>Tôn giáo:</Text>
                  <Text className={styles.infoValue}>{detailData.religion || 'Chưa cập nhật'}</Text>
                </div>
                <div className={styles.infoRow}>
                  <Text className={styles.infoLabel}>Nơi sinh:</Text>
                  <Text className={styles.infoValue}>{detailData.placeOfBirth || 'Chưa cập nhật'}</Text>
                </div>
                <div className={styles.infoRow}>
                  <Text className={styles.infoLabel}>Quốc tịch:</Text>
                  <Text className={styles.infoValue}>{detailData.nationality || 'Chưa cập nhật'}</Text>
                </div>
              </Col>
              <Col span={12}>
                <div className={styles.infoRow}>
                  <Text className={styles.infoLabel}>Nguyên quán:</Text>
                  <Text className={styles.infoValue}>
                    {detailData.hometown 
                      ? detailData.hometown.replace('Huyện ', '').replace('Tỉnh ', '').replace(', Việt Nam', '') 
                      : 'Chưa cập nhật'}
                  </Text>
                </div>
                <div className={styles.infoRow}>
                  <Text className={styles.infoLabel}>Thường trú:</Text>
                  <Text className={styles.infoValue}>{detailData.permanentAddress || 'Chưa cập nhật'}</Text>
                </div>
                <div className={styles.infoRow}>
                  <Text className={styles.infoLabel}>Địa chỉ:</Text>
                  <Text className={styles.infoValue}>{detailData.address || 'Chưa cập nhật'}</Text>
                </div>
                <div className={styles.infoRow}>
                  <Text className={styles.infoLabel}>Mã số thuế:</Text>
                  <Text className={styles.infoValue}>{detailData.taxCode || 'Chưa cập nhật'}</Text>
                </div>
              </Col>
            </Row>
          </div>

          <AdditionalPersonnelInfo data={detailData} />

          <div className={styles.section}>
            <Title level={4} className={styles.sectionTitle}>
              Thông tin ngân hàng
            </Title>
            <Row gutter={[24, 16]}>
              <Col span={12}>
                <div className={styles.infoRow}>
                  <Text className={styles.infoLabel}>Số Tài Khoản:</Text>
                  <Text className={styles.infoValue}>{detailData.bankAccount || 'Chưa cập nhật'}</Text>
                </div>
                <div className={styles.infoRow}>
                  <Text className={styles.infoLabel}>Tên người nhận:</Text>
                  <Text className={styles.infoValue}>{detailData.bankAccountHolder || 'Chưa cập nhật'}</Text>
                </div>
              </Col>
              <Col span={12}>
                <div className={styles.infoRow}>
                  <Text className={styles.infoLabel}>Ngân hàng:</Text>
                  <Text className={styles.infoValue}>{detailData.bankName || 'Chưa cập nhật'}</Text>
                </div>
                <div className={styles.infoRow}>
                  <Text className={styles.infoLabel}>Chi nhánh:</Text>
                  <Text className={styles.infoValue}>{detailData.bankBranch || 'Chưa cập nhật'}</Text>
                </div>
              </Col>
            </Row>
          </div>

          <div className={styles.section}>
            <Title level={4} className={styles.sectionTitle}>
              Công việc hiện nay
            </Title>
            {detailData.currentJob && (
              <Table
                columns={currentJobColumns}
                dataSource={[detailData.currentJob]}
                pagination={false}
                rowKey="company"
                className={styles.table}
              />
            )}
          </div>

          <div className={styles.section}>
            <Title level={4} className={styles.sectionTitle}>
              Học vấn & Kinh nghiệm
            </Title>
            {detailData.education && (
              <div className={styles.educationInfo}>
                <Row gutter={[24, 16]}>
                  <Col span={12}>
                    <div className={styles.infoRow}>
                      <Text className={styles.infoLabel}>Trình độ học vấn cao nhất:</Text>
                      <Text className={styles.infoValue}>
                        {formatFieldValue('highestEducationLevel', detailData.education.highestLevel) || 'Chưa cập nhật'}
                      </Text>
                    </div>
                  </Col>
                  <Col span={12}>
                    <div className={styles.infoRow}>
                      <Text className={styles.infoLabel}>Trình độ phổ thông:</Text>
                      <Text className={styles.infoValue}>
                        {formatFieldValue('generalEducationLevel', detailData.education.generalLevel) || 'Chưa cập nhật'}
                      </Text>
                    </div>
                  </Col>
                </Row>
                <Row gutter={[24, 16]}>
                  <Col span={12}>
                    <div className={styles.infoRow}>
                      <Text className={styles.infoLabel}>Chuyên ngành:</Text>
                      <Text className={styles.infoValue}>{detailData.education.major || 'Chưa cập nhật'}</Text>
                    </div>
                  </Col>
                  <Col span={12}>
                    <div className={styles.infoRow}>
                      <Text className={styles.infoLabel}>Số năm kinh nghiệm trước đây:</Text>
                      <Text className={styles.infoValue}>
                        {detailData.education.yearsOfExperience !== undefined && detailData.education.yearsOfExperience !== null
                          ? detailData.education.yearsOfExperience
                          : ''}
                      </Text>
                    </div>
                  </Col>
                </Row>
              </div>
            )}
            
            <div className={styles.subsection}>
              {detailData.trainingPlaces && (
                <Table
                  columns={trainingColumns}
                  dataSource={detailData.trainingPlaces}
                  pagination={false}
                  rowKey="place"
                  className={styles.table}
                />
              )}
            </div>

            <div className={styles.subsection}>
              {detailData.workExperience && (
                <Table
                  columns={experienceColumns}
                  dataSource={detailData.workExperience}
                  pagination={false}
                  rowKey="company"
                  className={styles.table}
                />
              )}
            </div>
          </div>

          <div className={styles.section}>
            <Title level={4} className={styles.sectionTitle}>
              Thông tin gia đình & người phụ thuộc
            </Title>
            {detailData.familyMembers && (
              <Table
                columns={familyColumns}
                dataSource={detailData.familyMembers}
                pagination={false}
                rowKey="name"
                className={styles.table}
              />
            )}
          </div>

          <div className={styles.section}>
            <Title level={4} className={styles.sectionTitle}>
              Chứng chỉ
            </Title>
            {detailData.certificates && (
              <Table
                columns={certificateColumns}
                dataSource={detailData.certificates}
                pagination={false}
                rowKey="id"
                className={styles.table}
              />
            )}

            <div style={{ marginTop: 16 }}>
              <Text className={styles.infoLabel}>Kỹ năng:</Text>
              <Text className={styles.infoValue} style={{ marginLeft: 8 }}>
                {detailData.skills && detailData.skills.length > 0
                  ? detailData.skills.join(', ')
                  : 'Chưa cập nhật'}
              </Text>
            </div>
          </div>

          <div className={styles.section}>
            <Title level={4} className={styles.sectionTitle}>
              Lịch sử đảng viên
            </Title>
            {detailData.partyHistory && (
              <Table
                columns={partyColumns}
                dataSource={detailData.partyHistory}
                pagination={false}
                rowKey="cardNumber"
                className={styles.table}
              />
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
